import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { 
  CheckSquare, 
  Bell, 
  DollarSign, 
  Gift, 
  Search, 
  Plus,
  AlertCircle,
  Loader2,
  Shield
} from 'lucide-react';
import { api, DashboardData, UserProfile } from '../lib/api';
import { toast } from 'sonner';
import LogRocket from 'logrocket';
import { PayPalFunds } from './PayPalFunds';

interface DashboardProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; memberId?: string; tab?: string; view?: string; suggestionId?: string }) => void;
  initialTab?: string;
  giftGroupId?: string | null;
  recommendedAmount?: number | null;
}

export function Dashboard({ onNavigate, initialTab = 'todo', giftGroupId, recommendedAmount }: DashboardProps) {
  console.log('📊 Dashboard render - initialTab:', initialTab);
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update active tab when initialTab changes
  useEffect(() => {
    console.log('📊 Dashboard useEffect - initialTab changed to:', initialTab);
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [dashboard, profile] = await Promise.all([
        api.getDashboard(),
        api.getProfile()
      ]);
      
      setDashboardData(dashboard);
      setUserProfile(profile);
      
      // Identify user in LogRocket for session tracking
      if (profile) {
        try {
          const identifyData: Record<string, string | number | boolean> = {
            name: profile.name || 'Unknown',
          };
          if (profile.email) identifyData.email = profile.email;
          if (profile.phone) identifyData.phone = profile.phone;
          
          console.log('[LogRocket] Identifying user:', profile.id, identifyData);
          LogRocket.identify(profile.id.toString(), identifyData);
          console.log('[LogRocket] User identified successfully');
        } catch (error) {
          console.error('[LogRocket] Failed to identify user:', error);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(errorMessage);
      toast.error('Failed to load dashboard', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Handle both ISO timestamps and YYYY-MM-DD dates
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleFundsAdded = () => {
    // Refresh dashboard data after successful payment
    loadDashboardData();
    
    // Switch to TODO tab to see gift group tasks
    setActiveTab('todo');
    
    // If this was for a specific gift group, provide feedback
    if (giftGroupId) {
      toast.success('Ready to view your gift groups!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center">
        <Card className="border border-primary/20 shadow-lg p-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !dashboardData || !userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="border border-primary/20 shadow-lg p-8 max-w-md w-full">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || 'Failed to load dashboard data'}
            </AlertDescription>
          </Alert>
          <Button onClick={loadDashboardData} className="w-full">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20">
      {/* Dashboard Header */}
      <div className="bg-background border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-primary mb-1">Gifter Dashboard</h1>
              <p className="text-muted-foreground text-sm truncate">
                Welcome back, {userProfile.name || userProfile.email || userProfile.phone}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto">
              {userProfile.adminLevel && (
                <Button
                  onClick={() => onNavigate('admin')}
                  variant="outline"
                  className="border-orange-500 text-orange-600 hover:bg-orange-50 flex-1 sm:flex-none"
                >
                  <Shield className="w-4 h-4 sm:mr-2" />
                  <span className="hidden xs:inline">Admin</span>
                  <span className="xs:hidden">Admin</span>
                </Button>
              )}
              <Button
                onClick={() => onNavigate('create-gift-group')}
                className="bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden xs:inline">Create Gift Group</span>
                <span className="xs:hidden">Create</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-auto bg-background/80 border border-primary/20 overflow-x-auto">
            <TabsTrigger value="todo" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">To-Do</span>
              {dashboardData.todos.length > 0 && (
                <Badge variant="destructive" className="text-[10px] sm:text-xs">{dashboardData.todos.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="communications" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs hidden xs:inline">Comments</span>
              <span className="text-[10px] sm:text-xs xs:hidden">💬</span>
              {dashboardData.communications.length > 0 && (
                <Badge variant="secondary" className="text-[10px] sm:text-xs">{dashboardData.communications.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="finances" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">Money</span>
            </TabsTrigger>
            <TabsTrigger value="gift-groups" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs">Groups</span>
              {dashboardData.giftGroups.length > 0 && (
                <Badge variant="outline" className="text-[10px] sm:text-xs">{dashboardData.giftGroups.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="search" className="flex-col gap-1 py-2 sm:py-3 px-2">
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs">Search Groups</span>
            </TabsTrigger>
          </TabsList>

          {/* To-Do Tab */}
          <TabsContent value="todo" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">Outstanding Tasks</h2>
              </div>
              
              {dashboardData.todos.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No outstanding tasks! 🎉</p>
                  <p className="text-sm mt-1">You're all caught up.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Gift Group</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Date</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.todos.map((todo) => (
                        <TableRow key={todo.member_id}>
                          <TableCell className="max-w-[150px] md:max-w-none truncate">{todo.gift_group_name}</TableCell>
                          <TableCell className="whitespace-nowrap">{formatDate(todo.due_date)}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => {
                                if (todo.type === 'accept-invitation') {
                                  onNavigate('gift-group-acceptance', { memberId: todo.member_id });
                                } else if (todo.type === 'contribute') {
                                  onNavigate('gift-group-contribution', { giftGroupId: todo.gift_group_id });
                                } else if (todo.type === 'explore-ai-gifts') {
                                  onNavigate('gift-group-overview', { 
                                    giftGroupId: todo.gift_group_id,
                                    view: 'ai-suggestions'
                                  });
                                } else if (todo.type === 'add-manual-gifts') {
                                  onNavigate('gift-group-overview', { 
                                    giftGroupId: todo.gift_group_id,
                                    view: 'manual-suggestions'
                                  });
                                } else {
                                  // rank-gifts and others default to ranking view
                                  onNavigate('gift-group-overview', { giftGroupId: todo.gift_group_id });
                                }
                              }}
                              className="whitespace-nowrap"
                            >
                              {todo.type === 'accept-invitation' && 'Accept Invitation'}
                              {todo.type === 'contribute' && 'Contribute Funds'}
                              {todo.type === 'explore-ai-gifts' && 'Explore AI Gifts'}
                              {todo.type === 'add-manual-gifts' && 'Add Manual Gifts'}
                              {todo.type === 'rank-gifts' && 'Rank Gifts'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">Gift Comments & Mentions</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Comments on your gift suggestions and when you're @mentioned
                </p>
              </div>
              
              {dashboardData.communications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No new comments or mentions</p>
                  <p className="text-sm mt-1">When someone comments on your gift suggestions or @mentions you, you'll see them here!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Gift</TableHead>
                        <TableHead className="whitespace-nowrap">Comment</TableHead>
                        <TableHead className="whitespace-nowrap">From</TableHead>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.communications.map((comm) => {
                        const commenterName = (comm as any).commenter_name || (comm as any).commenter_email || (comm as any).commenter_phone || 'Someone';
                        return (
                          <TableRow key={comm.id}>
                            <TableCell className="max-w-[150px] md:max-w-none truncate">
                              {(comm as any).gift_name || 'Unknown Gift'}
                            </TableCell>
                            <TableCell className="max-w-[200px] md:max-w-none truncate">{comm.message}</TableCell>
                            <TableCell className="whitespace-nowrap">{commenterName}</TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(comm.created_at)}</TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-primary/30 hover:bg-primary/5 whitespace-nowrap"
                                onClick={() => {
                                  // Navigate to gift group with suggestion_id
                                  onNavigate('gift-group-overview', { 
                                    giftGroupId: comm.gift_group_id,
                                    suggestionId: (comm as any).suggestion_id 
                                  });
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Finances Tab */}
          <TabsContent value="finances" className="space-y-4">
            {/* Balance Summary Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Current Credit Balance</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.creditBalance)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Available for contributions
                </div>
              </Card>
              
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Allocated to Gift Groups</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.allocated)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {dashboardData.giftGroups.filter(g => g.member_status === 'contributed').length} active groups
                </div>
              </Card>
              
              <Card className="border border-primary/20 shadow-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Upcoming Budget</div>
                  <div className="text-primary">{formatCurrency(dashboardData.finances.upcoming90Days)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">Next 90 days</div>
              </Card>
            </div>

            {/* PayPal Add Funds */}
            <PayPalFunds 
              giftGroupId={giftGroupId}
              recommendedAmount={recommendedAmount}
              onSuccess={handleFundsAdded}
            />

            {/* Test Funds Button (Development Only) */}
            {import.meta.env.DEV && (
              <Card className="border border-primary/20 shadow-lg p-6">
                <div className="mb-4">
                  <h3 className="text-primary mb-2">Development Testing</h3>
                  <p className="text-sm text-muted-foreground">
                    Add test funds without payment (development only)
                  </p>
                </div>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90" 
                  size="sm"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      const result = await api.addTestFunds(100);
                      toast.success('Test funds added!', {
                        description: `Added $100. New balance: ${formatCurrency(result.newBalance)}`,
                      });
                      loadDashboardData();
                    } catch (err) {
                      toast.error('Failed to add test funds');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add $100 (Test)'
                  )}
                </Button>
              </Card>
            )}

            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">Transaction Ledger</h2>
              </div>
              
              {dashboardData.finances.recentTransactions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Description</TableHead>
                        <TableHead className="whitespace-nowrap">Reference</TableHead>
                        <TableHead className="whitespace-nowrap">Amount</TableHead>
                        <TableHead className="whitespace-nowrap">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.finances.recentTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="whitespace-nowrap">{formatDate(tx.created_at)}</TableCell>
                          <TableCell className="max-w-[150px] md:max-w-none truncate">{tx.description}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono max-w-[120px] truncate" title={tx.payment_provider_id || ''}>
                            {tx.payment_provider_id ? (
                              <span>
                                {tx.payment_provider && <span className="text-primary">{tx.payment_provider}: </span>}
                                {tx.payment_provider_id}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                          <TableCell className={`whitespace-nowrap ${tx.amount > 0 ? 'text-primary' : 'text-destructive'}`}>
                            {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formatCurrency(tx.balance_after)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Gift Groups Tab */}
          <TabsContent value="gift-groups" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h2 className="text-primary">My Gift Groups</h2>
              </div>
              
              {dashboardData.giftGroups.filter((group) => group.role !== 'giftee').length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No gift groups yet</p>
                  <p className="text-sm mt-1">Create your first gift group to get started!</p>
                  <Button 
                    className="mt-4 bg-primary hover:bg-primary/90"
                    onClick={() => onNavigate('create-gift-group')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Gift Group
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Gift Group</TableHead>
                        <TableHead className="whitespace-nowrap">Type</TableHead>
                        <TableHead className="whitespace-nowrap">Gift Date</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.giftGroups.filter((group) => group.role !== 'giftee').map((group) => {
                        // Determine workflow state button text and variant
                        const workflowState = group.workflow_state || 'pending_acceptance';
                        let buttonText = 'View';
                        let buttonVariant = 'outline' as 'default' | 'destructive' | 'outline';
                        
                        switch (workflowState) {
                          case 'pending_acceptance':
                            buttonText = 'Accept Invitation';
                            buttonVariant = 'default';
                            break;
                          case 'pending_contribution':
                            buttonText = 'Contribute Funds';
                            buttonVariant = 'default';
                            break;
                          case 'pending_ai_gifts':
                            buttonText = 'Explore AI Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'pending_manual_gifts':
                            buttonText = 'Add Manual Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'pending_ranking':
                            buttonText = 'Rank Gifts';
                            buttonVariant = 'default';
                            break;
                          case 'active':
                            buttonText = 'View Details';
                            buttonVariant = 'outline';
                            break;
                          default:
                            buttonText = 'View';
                            buttonVariant = 'outline';
                        }

                        return (
                          <TableRow key={group.id}>
                            <TableCell className="truncate max-w-[150px] md:max-w-none">{group.title}</TableCell>
                            <TableCell className="whitespace-nowrap">{group.type_name}</TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(group.gift_date)}</TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant={buttonVariant}
                                onClick={() => {
                                  if (workflowState === 'pending_acceptance') {
                                    onNavigate('gift-group-acceptance', { memberId: group.id });
                                  } else if (workflowState === 'pending_contribution') {
                                    onNavigate('gift-group-contribution', { giftGroupId: group.id });
                                  } else if (workflowState === 'pending_ai_gifts') {
                                    onNavigate('gift-group-overview', { 
                                      giftGroupId: group.id,
                                      view: 'ai-suggestions'
                                    });
                                  } else if (workflowState === 'pending_manual_gifts') {
                                    onNavigate('gift-group-overview', { 
                                      giftGroupId: group.id,
                                      view: 'manual-suggestions'
                                    });
                                  } else {
                                    // rank-gifts and other states default to ranking view
                                    onNavigate('gift-group-overview', { giftGroupId: group.id });
                                  }
                                }}
                                className="whitespace-nowrap"
                              >
                                {buttonText}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search" className="space-y-4">
            <Card className="border border-primary/20 shadow-lg p-6">
              <h2 className="text-primary mb-4">Search for Gift Groups</h2>
              <p className="text-sm text-muted-foreground mb-6">Find and join existing gift groups</p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="space-y-2">
                  <label className="text-sm">Giftee Name</label>
                  <Input placeholder="Enter name..." />
                </div>
                <div className="space-y-2">
                  <label className="text-sm">Event Title</label>
                  <Input placeholder="Enter event..." />
                </div>
                <div className="space-y-2">
                  <label className="text-sm">Event Date</label>
                  <Input type="date" />
                </div>
              </div>
              
              <Button className="w-full md:w-auto bg-primary hover:bg-primary/90">
                <Search className="w-4 h-4 mr-2" />
                Search Gift Groups
              </Button>
            </Card>

            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20">
                <h3 className="text-primary">Search Results</h3>
              </div>
              
              <div className="p-6 text-center text-muted-foreground">
                Enter search criteria above to find gift groups
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}